import { useState, useEffect, useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapImprovementClarificationList } from '../utils/improvementClarificationHelpers';

export const buildImprovementClarificationListUrl = ({
  currentPage,
  rowsPerPage,
  procurementTypeHeader,
  search,
  statusImprovement,
  statusClarification,
  sortField,
  sortDirection,
}) => {
  const params = new URLSearchParams();
  params.set('PageNumber', String(currentPage));
  params.set('PageSize', String(rowsPerPage));

  if (procurementTypeHeader) {
    params.set('ProcurementTypeHeader', procurementTypeHeader);
  }

  if (search?.trim()) {
    params.set('Search', search.trim());
  }

  if (statusImprovement) {
    params.set('StatusImprovement', statusImprovement);
  }

  if (statusClarification) {
    params.set('StatusClarification', statusClarification);
  }

  if (sortField) {
    params.set('SortBy', sortField);
    params.set('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
  }

  return `${api.API_URL}/procurement/improvement-and-clarification/mitra?${params.toString()}`;
};

const useImprovementClarificationList = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  enabled = true,
}) => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusImprovementFilter, setStatusImprovementFilter] = useState('');
  const [statusClarificationFilter, setStatusClarificationFilter] =
    useState('');
  const [sortField, setSortField] = useState('procurementNumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    void refreshTrigger;
    if (!enabled) return;

    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsLoading(true);

    try {
      const url = buildImprovementClarificationListUrl({
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        statusImprovement: statusImprovementFilter,
        statusClarification: statusClarificationFilter,
        sortField,
        sortDirection,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const { items, totalItems } = unwrapImprovementClarificationList(result);

      setData({ items });
      setTotalRows(totalItems);
    } catch (error) {
      notify.apiError(error, 'load improvement clarification data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    statusImprovementFilter,
    statusClarificationFilter,
    sortField,
    sortDirection,
    procurementTypeHeader,
    enabled,
    refreshTrigger,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = e => setSearchText(e.target.value);

  const handleStatusImprovementFilter = value => {
    setStatusImprovementFilter(value);
    setCurrentPage(1);
  };

  const handleStatusClarificationFilter = value => {
    setStatusClarificationFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    const fieldMapping = {
      idProc: 'procurementNumber',
      title: 'titleProcurement',
      procType: 'procurementType',
      productType: 'productType',
      description: 'description',
      statusImprovement: 'statusImprovementProgressLabel',
      statusClarification: 'statusClarificationProgressLabel',
    };
    setSortField(fieldMapping[column.id] || column.id || 'procurementNumber');
    setSortDirection(direction);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusImprovementFilter('');
    setStatusClarificationFilter('');
    setSortField('procurementNumber');
    setSortDirection('desc');
    setCurrentPage(1);
  };

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  return {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    statusImprovementFilter,
    statusClarificationFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusImprovementFilter,
    handleStatusClarificationFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
    debouncedSearchText,
    sortField,
    sortDirection,
    refresh: fetchData,
  };
};

export default useImprovementClarificationList;
